/**
 * Copyright 2025 John Huikku Alienrobot LLC
 * www.ALIENROBOT.com
 */

import { useState } from 'react'
import { Brain, Code, Palette, FileText, Briefcase, X, TrendingUp } from 'lucide-react'
import { OPENROUTER_MODELS } from '@/providers/llm-provider'
import { PERSONA_LIBRARY, PERSONA_CATEGORIES, getPersonasByCategory, generateSystemPrompt, type PersonaTemplate } from '@/core/personas'
import type { PeerConfig } from '@/lib/storage'

interface ProjectSetupModalProps {
  onComplete: (projectName: string, peers: PeerConfig[], orchestratorModel?: string, judgeModel?: string) => void
  onCancel: () => void
}

interface ProjectGoal {
  id: string
  name: string
  description: string
  icon: any
  defaultPersonaIds: string[] // IDs from persona library
}

const PROJECT_GOALS: ProjectGoal[] = [
  {
    id: 'general',
    name: 'General Purpose',
    description: 'Balanced team for diverse tasks - analysis, content, and innovation',
    icon: Brain,
    defaultPersonaIds: ['Technical_Reviewer', 'Content_Editor', 'Innovation_Advisor'],
  },
  {
    id: 'software',
    name: 'Software Development',
    description: 'Code review, architecture, and system design specialists',
    icon: Code,
    defaultPersonaIds: ['Frontend_Developer', 'Backend_Developer', 'DevOps_Engineer'],
  },
  {
    id: 'writing',
    name: 'Content Writing',
    description: 'Editorial team for articles, documentation, and creative writing',
    icon: FileText,
    defaultPersonaIds: ['Content_Editor', 'Technical_Reviewer', 'Copywriter'],
  },
  {
    id: 'business',
    name: 'Business Strategy',
    description: 'Strategic planning, market analysis, and business development',
    icon: Briefcase,
    defaultPersonaIds: ['System_Architect', 'Research_Scientist', 'Innovation_Advisor'],
  },
  {
    id: 'design',
    name: 'Creative Design',
    description: 'UX/UI design, branding, and visual communication',
    icon: Palette,
    defaultPersonaIds: ['UI_Designer', 'UX_Researcher', 'Content_Editor'],
  },
  {
    id: 'research',
    name: 'Research & Analysis',
    description: 'Scientific research, data analysis, and evidence-based reasoning',
    icon: Brain,
    defaultPersonaIds: ['Research_Scientist', 'Data_Analyst', 'Technical_Reviewer'],
  },
]

// Fallback personas if specific ones aren't found
const FALLBACK_PERSONAS = ['Technical_Reviewer', 'Content_Editor', 'Innovation_Advisor']

// Helper to get personas from library by IDs
function getPersonasByIds(ids: string[]): PersonaTemplate[] {
  const personas = ids.map(id => PERSONA_LIBRARY.find(p => p.id === id)).filter(Boolean) as PersonaTemplate[]

  // If we didn't find all personas, use fallbacks
  if (personas.length < 3) {
    return FALLBACK_PERSONAS.map(id => PERSONA_LIBRARY.find(p => p.id === id)).filter(Boolean) as PersonaTemplate[]
  }

  return personas
}

export function ProjectSetupModal({ onComplete, onCancel }: ProjectSetupModalProps) {
  const [step, setStep] = useState<'name' | 'goal' | 'customize'>('name')
  const [projectName, setProjectName] = useState('')
  const [selectedGoal, setSelectedGoal] = useState<ProjectGoal | null>(null)
  const [selectedPersonas, setSelectedPersonas] = useState<PersonaTemplate[]>([])
  const [selectedModels, setSelectedModels] = useState<string[]>([])
  const [orchestratorModel, setOrchestratorModel] = useState('anthropic/claude-sonnet-4-5')
  const [judgeModel, setJudgeModel] = useState('anthropic/claude-sonnet-4-5')

  const handleGoalSelect = (goal: ProjectGoal) => {
    setSelectedGoal(goal)
    const personas = getPersonasByIds(goal.defaultPersonaIds)
    setSelectedPersonas(personas)
    // Initialize models with recommended models or default
    setSelectedModels(personas.map(p => p.recommendedModels[0] || 'google/gemini-2.5-pro'))
    setStep('customize')
  }

  const handlePersonaChange = (index: number, newPersonaId: string) => {
    const newPersona = PERSONA_LIBRARY.find(p => p.id === newPersonaId)
    if (newPersona) {
      const updated = [...selectedPersonas]
      updated[index] = newPersona
      setSelectedPersonas(updated)
      // Update model to persona's recommended model
      const newModels = [...selectedModels]
      newModels[index] = newPersona.recommendedModels[0] || 'google/gemini-2.5-pro'
      setSelectedModels(newModels)
    }
  }

  const handleModelChange = (index: number, modelId: string) => {
    const newModels = [...selectedModels]
    newModels[index] = modelId
    setSelectedModels(newModels)
  }

  const handleQuickTierSelection = (tier: 'ultra-cheap-us' | 'cheapest' | 'budget' | 'premium' | 'google-free') => {
    const tierModels: Record<string, string> = {
      'ultra-cheap-us': 'groq/llama-3.3-70b-versatile',
      'cheapest': 'deepseek/deepseek-chat',
      'budget': 'google/gemini-2.0-flash-exp',
      'premium': 'anthropic/claude-sonnet-4-5',
      'google-free': 'google/gemini-2.5-pro',
    }
    const model = tierModels[tier]

    // For Google preset, use varied models
    if (tier === 'google-free') {
      setSelectedModels([
        'google/gemini-3-pro-preview',
        'google/gemini-2.5-pro',
        'google/gemini-2.0-flash-exp'
      ])
      setOrchestratorModel('google/gemini-2.5-pro')
      setJudgeModel('google/gemini-2.5-pro')
    } else {
      setSelectedModels(selectedPersonas.map(() => model))
      setOrchestratorModel(model)
      setJudgeModel(model)
    }
  }

  const handleComplete = () => {
    if (!projectName || selectedPersonas.length === 0) return

    const peers: PeerConfig[] = selectedPersonas.map((persona, index) => ({
      id: `peer-${index + 1}`,
      personaId: persona.id,
      personaName: persona.name,
      model: selectedModels[index] || persona.recommendedModels[0] || 'google/gemini-2.5-pro',
      systemPrompt: generateSystemPrompt(persona),
      enabled: true,
      collapsed: false,
      color: index === 0 ? 'text-purple-400' : index === 1 ? 'text-orange-400' : 'text-green-400',
      icon: index === 0 ? 'gemini' : index === 1 ? 'claude' : 'grok',
    }))

    onComplete(projectName, peers, orchestratorModel, judgeModel)
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-background-secondary border border-border rounded-lg w-[900px] max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-background-secondary border-b border-border p-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-title">Create New Project</h2>
            <p className="text-xs text-foreground-muted mt-1">
              {step === 'name' && 'Choose a name for your project'}
              {step === 'goal' && 'Select your project type to get recommended personas'}
              {step === 'customize' && 'Customize your peer team from the persona library'}
            </p>
          </div>
          <button
            onClick={onCancel}
            className="p-1 hover:bg-background-tertiary rounded transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {step === 'name' && (
            <div className="space-y-4 max-w-md mx-auto">
              <div>
                <label className="block text-sm font-semibold mb-2">Project Name</label>
                <input
                  type="text"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  placeholder="My Awesome Project"
                  className="input w-full"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && projectName.trim()) {
                      setStep('goal')
                    }
                  }}
                />
              </div>
            </div>
          )}

          {step === 'goal' && (
            <div className="grid grid-cols-2 gap-4">
              {PROJECT_GOALS.map((goal) => {
                const Icon = goal.icon
                const personas = getPersonasByIds(goal.defaultPersonaIds)
                return (
                  <button
                    key={goal.id}
                    onClick={() => handleGoalSelect(goal)}
                    className="text-left p-5 border border-border rounded-lg hover:border-accent hover:bg-background-tertiary transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <Icon className="w-7 h-7 text-accent flex-shrink-0 mt-1" />
                      <div className="flex-1">
                        <h3 className="font-semibold text-base mb-1">{goal.name}</h3>
                        <p className="text-xs text-foreground-muted mb-3">{goal.description}</p>
                        <div className="text-xs text-foreground-secondary">
                          <span className="font-semibold">Team:</span>
                          {personas.map((persona, idx) => (
                            <span key={persona.id}>
                              {idx > 0 && ', '}
                              {persona.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </button>
                )
              })}
            </div>
          )}

          {step === 'customize' && selectedGoal && (
            <div className="space-y-4">
              <div className="bg-background-tertiary border border-border rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="text-sm font-semibold">Quick Model Selection</h3>
                    <p className="text-xs text-foreground-muted mt-1">
                      Set all peers to the same tier for consistent quality/cost
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => handleQuickTierSelection('google-free')}
                    className="btn-primary text-xs py-1 px-3"
                    title="FREE! Google AI Studio API - 1500 requests/day free tier. Get your key at aistudio.google.com"
                  >
                    🆓 Google Free Tier
                  </button>
                  <button
                    onClick={() => handleQuickTierSelection('ultra-cheap-us')}
                    className="btn-secondary text-xs py-1 px-3"
                    title="Ultra-cheap US models: ~$0.20/M tokens (Groq Llama 3.1 70B)"
                  >
                    💰 Ultra-Cheap (US)
                  </button>
                  <button
                    onClick={() => handleQuickTierSelection('cheapest')}
                    className="btn-secondary text-xs py-1 px-3"
                    title="Absolute cheapest: ~$0.02/M tokens (DeepSeek Chat V3)"
                  >
                    💸 Cheapest Overall
                  </button>
                  <button
                    onClick={() => handleQuickTierSelection('budget')}
                    className="btn-secondary text-xs py-1 px-3"
                    title="Budget tier: $0.15-1.20/M tokens (Gemini 2.0 Flash, GPT-4o Mini)"
                  >
                    💰 Budget
                  </button>
                  <button
                    onClick={() => handleQuickTierSelection('premium')}
                    className="btn-secondary text-xs py-1 px-3"
                    title="Premium tier: $1.25-15/M tokens (Claude Sonnet 4.5, GPT-5, Gemini 2.5 Pro)"
                  >
                    ⭐ Premium
                  </button>
                </div>
              </div>

              <p className="text-sm text-foreground-muted">
                Configure models for Orchestrator, Judge, and your peer team.
              </p>

              {/* Orchestrator and Judge Model Selectors */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bento-tile">
                  <div className="bento-tile-header">
                    <div className="flex items-center gap-2">
                      <Brain className="w-4 h-4 text-blue-400" />
                      <span className="text-sm font-semibold">Orchestrator</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="block text-xs font-semibold text-foreground-secondary">Model</label>
                    <select
                      value={orchestratorModel}
                      onChange={(e) => setOrchestratorModel(e.target.value)}
                      className="input w-full text-sm"
                    >
                      <optgroup label="💎 Premium - Best in Class">
                        <option value="anthropic/claude-sonnet-4-5">Claude Sonnet 4.5 - RECOMMENDED</option>
                        <option value="google/gemini-3-pro-preview">Gemini 3 Pro - THINKING MODE 🧠</option>
                        <option value="google/gemini-3-pro-preview-11-2025-thinking">Gemini 3 Pro Thinking - DEEP REASONING 🧠</option>
                        <option value="openai/gpt-5">GPT-5</option>
                        <option value="google/gemini-2.5-pro">Gemini 2.5 Pro</option>
                      </optgroup>
                      <optgroup label="💰 Budget">
                        <option value="google/gemini-2.0-flash-exp">Gemini 2.0 Flash</option>
                        <option value="openai/gpt-4o-mini">GPT-4o Mini</option>
                      </optgroup>
                      <optgroup label="💸 Ultra-Cheap">
                        <option value="groq/llama-3.3-70b-versatile">Groq Llama 3.3 70B</option>
                        <option value="deepseek/deepseek-chat">DeepSeek Chat V3</option>
                      </optgroup>
                    </select>
                  </div>
                </div>

                <div className="bento-tile">
                  <div className="bento-tile-header">
                    <div className="flex items-center gap-2">
                      <Brain className="w-4 h-4 text-yellow-400" />
                      <span className="text-sm font-semibold">Judge</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="block text-xs font-semibold text-foreground-secondary">Model</label>
                    <select
                      value={judgeModel}
                      onChange={(e) => setJudgeModel(e.target.value)}
                      className="input w-full text-sm"
                    >
                      <optgroup label="💎 Premium - Best in Class">
                        <option value="anthropic/claude-sonnet-4-5">Claude Sonnet 4.5 - RECOMMENDED</option>
                        <option value="google/gemini-3-pro-preview">Gemini 3 Pro - THINKING MODE 🧠</option>
                        <option value="google/gemini-3-pro-preview-11-2025-thinking">Gemini 3 Pro Thinking - DEEP REASONING 🧠</option>
                        <option value="openai/gpt-5">GPT-5</option>
                        <option value="google/gemini-2.5-pro">Gemini 2.5 Pro</option>
                      </optgroup>
                      <optgroup label="💰 Budget">
                        <option value="google/gemini-2.0-flash-exp">Gemini 2.0 Flash</option>
                        <option value="openai/gpt-4o-mini">GPT-4o Mini</option>
                      </optgroup>
                      <optgroup label="💸 Ultra-Cheap">
                        <option value="groq/llama-3.3-70b-versatile">Groq Llama 3.3 70B</option>
                        <option value="deepseek/deepseek-chat">DeepSeek Chat V3</option>
                      </optgroup>
                    </select>
                  </div>
                </div>
              </div>

              <p className="text-sm text-foreground-muted mt-4">
                Configure your peer team personas and models.
              </p>

              {selectedPersonas.map((persona, index) => (
                <div key={persona.id} className="bento-tile">
                  {/* Header with Persona Name */}
                  <div className="bento-tile-header">
                    <div className="flex items-center gap-2">
                      <Brain className="w-4 h-4 text-accent" />
                      <span className="text-sm font-semibold">Peer {index + 1}</span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-foreground-secondary mb-1.5">Persona</label>
                        <select
                          value={persona.id}
                          onChange={(e) => handlePersonaChange(index, e.target.value)}
                          className="input w-full text-sm"
                        >
                          {PERSONA_CATEGORIES.map((category) => {
                            const categoryPersonas = getPersonasByCategory(category)
                            return (
                              <optgroup key={category} label={category}>
                                {categoryPersonas.map((p) => (
                                  <option key={p.id} value={p.id}>
                                    {p.name}
                                  </option>
                                ))}
                              </optgroup>
                            )
                          })}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-foreground-secondary mb-1.5">Model</label>
                        <select
                          value={selectedModels[index] || persona.recommendedModels[0] || 'google/gemini-2.5-pro'}
                          onChange={(e) => handleModelChange(index, e.target.value)}
                          className="input w-full text-sm"
                        >
                          <optgroup label="💎 Premium - Best in Class ($3-15/M tokens)">
                            <option value="anthropic/claude-sonnet-4-5">Claude Sonnet 4.5 (200K, $3-15/M) - BEST OVERALL</option>
                            <option value="openai/gpt-5">GPT-5 (128K, $5-15/M)</option>
                            <option value="anthropic/claude-3-opus-20240229">Claude 3 Opus (200K, $15-75/M)</option>
                          </optgroup>
                          <optgroup label="⭐ Premium Affordable ($1-5/M tokens)">
                            <option value="google/gemini-2.5-pro">Gemini 2.5 Pro (2M context!, $1.25-5/M) - RECOMMENDED</option>
                            <option value="anthropic/claude-3-5-sonnet-20241022">Claude 3.5 Sonnet (200K, $3-15/M)</option>
                            <option value="openai/gpt-4o">GPT-4o (128K, $2.50-10/M)</option>
                            <option value="openai/gpt-4-turbo">GPT-4 Turbo (128K, $10-30/M)</option>
                          </optgroup>
                          <optgroup label="💰 Budget - Great Value ($0.15-1/M tokens)">
                            <option value="google/gemini-2.0-flash-exp">Gemini 2.0 Flash (1M context!, $0.30-1.20/M) - BEST BUDGET</option>
                            <option value="openai/gpt-4o-mini">GPT-4o Mini (128K, $0.15-0.60/M)</option>
                            <option value="anthropic/claude-3-haiku-20240307">Claude 3 Haiku (200K, $0.80-4/M)</option>
                          </optgroup>
                          <optgroup label="💸 Ultra-Cheap - US Based ($0.18-0.20/M tokens)">
                            <option value="groq/llama-3.3-70b-versatile">Groq Llama 3.3 70B (128K, $0.20/M) - BLAZING FAST</option>
                            <option value="together/meta-llama/Meta-Llama-3.1-70B-Instruct-Turbo">Together Llama 3.1 70B (131K, $0.18/M)</option>
                            <option value="groq/mixtral-8x7b-32768">Groq Mixtral 8x7B (32K, $0.20/M)</option>
                          </optgroup>
                          <optgroup label="🔥 Ultra-Cheap - Absolute Lowest ($0.01-0.10/M tokens)">
                            <option value="deepseek/deepseek-chat">DeepSeek Chat V3 (64K, $0.014-0.028/M) - CHEAPEST</option>
                            <option value="qwen/qwen3-235b-a22b">Qwen 3 235B-A22B MoE (128K, $0.10/M) - THINKING MODE 🧠</option>
                            <option value="qwen/qwen3-30b-a3b">Qwen 3 30B-A3B MoE (128K, $0.08/M) - THINKING MODE 🧠</option>
                            <option value="qwen/qwen3-32b">Qwen 3 32B (128K, $0.09/M) - NEW 2025</option>
                            <option value="siliconflow/Qwen/Qwen2.5-72B-Instruct">SiliconFlow Qwen 2.5 72B (32K, $0.086/M)</option>
                            <option value="together/meta-llama/Meta-Llama-3.1-8B-Instruct-Turbo">Together Llama 3.1 8B (131K, $0.06/M)</option>
                            <option value="siliconflow/Qwen/Qwen2.5-7B-Instruct">SiliconFlow Qwen 2.5 7B (32K, $0.05/M)</option>
                          </optgroup>
                          <optgroup label="🔓 Open-Source - Large Models">
                            <option value="meta-llama/llama-3.1-405b-instruct">Llama 3.1 405B (128K)</option>
                            <option value="meta-llama/llama-3.1-70b-instruct">Llama 3.1 70B (128K)</option>
                            <option value="qwen/qwen-2.5-72b-instruct">Qwen 2.5 72B (32K)</option>
                            <option value="qwen/qwen3-14b">Qwen 3 14B (128K) - NEW 2025</option>
                            <option value="qwen/qwen3-8b">Qwen 3 8B (128K) - NEW 2025</option>
                          </optgroup>
                          <optgroup label="🌐 All Models (Alphabetical)">
                            {OPENROUTER_MODELS.map((model) => (
                              <option key={model.id} value={model.id}>
                                {model.name} ({model.provider})
                              </option>
                            ))}
                          </optgroup>
                        </select>
                        <p className="text-[10px] text-foreground-muted mt-1">
                          💡 Recommended: {persona.recommendedModels[0] || 'Default'}
                        </p>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-foreground-secondary mb-1.5">Role</label>
                      <div className="text-sm text-foreground bg-background-tertiary p-2.5 rounded border border-border">
                        {persona.role}
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-foreground-secondary mb-1.5">Description</label>
                      <div className="text-xs text-foreground-muted bg-background-tertiary p-2.5 rounded border border-border">
                        {persona.description}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-border p-4 flex justify-between">
          <button onClick={onCancel} className="btn-ghost px-4 py-2">
            Cancel
          </button>
          <div className="flex gap-2">
            {step === 'goal' && (
              <button onClick={() => setStep('name')} className="btn-secondary px-4 py-2">
                Back
              </button>
            )}
            {step === 'customize' && (
              <button onClick={() => setStep('goal')} className="btn-secondary px-4 py-2">
                Back
              </button>
            )}
            {step === 'name' && (
              <button
                onClick={() => setStep('goal')}
                disabled={!projectName.trim()}
                className="btn-primary px-4 py-2"
              >
                Next
              </button>
            )}
            {step === 'customize' && (
              <button onClick={handleComplete} className="btn-primary px-4 py-2">
                Create Project
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
